import {
  CheckCircleOutline,
  Dns,
  ErrorOutline,
  Replay,
  Sensors,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../../../../services/api";
import {
  DEFAULT_SNMP_PORT,
  DEFAULT_SNMP_VERSION,
  RouterPayload,
  RouterTestType,
  TestsLocalState,
  emptyTestsLocal,
} from "../../types";
import CardConnectivity, {
  ConnectivityTestData,
  ConnectivityTestIcon,
} from "./cardConnectivity";

interface TestConfig {
  type: RouterTestType;
  title: string;
  icon: ConnectivityTestIcon;
}

interface TestState {
  loading: boolean;
  data: ConnectivityTestData;
}

type TestsState = Record<RouterTestType, TestState>;

const TESTS: TestConfig[] = [
  { type: "ping", title: "Ping", icon: "PING" },
  { type: "snmp", title: "SNMP", icon: "SNMP" },
  { type: "pacotes", title: "Chegada de Pacotes", icon: "PACOTES" },
];

const buildInitialTests = (): TestsState =>
  TESTS.reduce((acc, { type, title, icon }) => {
    acc[type] = { loading: false, data: { title, type: icon } };
    return acc;
  }, {} as TestsState);

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { error?: string; message?: string }
      | undefined;
    return data?.error || data?.message || error.message || fallback;
  }
  return fallback;
};

export interface ConnectivityTestsProps {
  form: RouterPayload;
  setTestsLocal: React.Dispatch<React.SetStateAction<TestsLocalState>>;
  onTestingChange: (testing: boolean) => void;
}

export default function ConnectivityTests(props: ConnectivityTestsProps) {
  const { form, setTestsLocal, onTestingChange } = props;
  const theme = useTheme();

  const abortControllerRef = useRef<AbortController | null>(null);
  const [tests, setTests] = useState<TestsState>(buildInitialTests);

  const isTesting = useMemo(
    () => TESTS.some(({ type }) => tests[type].loading),
    [tests],
  );

  useEffect(() => {
    onTestingChange(isTesting);
  }, [isTesting, onTestingChange]);

  const updateTest = useCallback(
    (type: RouterTestType, partial: Partial<TestState>) => {
      setTests((prev) => ({
        ...prev,
        [type]: { ...prev[type], ...partial },
      }));
    },
    [],
  );

  const runTest = useCallback(
    async (config: TestConfig, signal: AbortSignal) => {
      const { type, title, icon } = config;
      updateTest(type, { loading: true, data: { title, type: icon } });

      try {
        const { data } = await api.post(
          "/easyFlow2.0/routers/test",
          {
            ipRouter: form?.sourceIp?.trim(),
            snmpIp: form?.snmpIp?.trim(),
            snmpCommunity: form?.snmpCommunity?.trim(),
            snmpPort: Number(form?.snmpPort) || DEFAULT_SNMP_PORT,
            snmpVersion: DEFAULT_SNMP_VERSION,
            type,
          },
          { signal },
        );

        const result = data?.data?.[type];
        const status = Boolean(result?.success);

        updateTest(type, {
          loading: false,
          data: {
            title,
            type: icon,
            status,
            message:
              result?.message ||
              data?.data?.message ||
              data?.data?.status ||
              (status ? `${title} OK` : `Falha no teste de ${title}`),
            timerExec:
              type === "ping"
                ? result?.avgPing || result?.timeExec
                : result?.timeExec,
          },
        });
        setTestsLocal((prev) => ({ ...prev, [type]: status }));
      } catch (error) {
        if (axios.isCancel(error)) return;

        updateTest(type, {
          loading: false,
          data: {
            title,
            type: icon,
            status: false,
            message: getErrorMessage(error, `Erro no teste de ${title}`),
          },
        });
        setTestsLocal((prev) => ({ ...prev, [type]: false }));
      }
    },
    [form, setTestsLocal, updateTest],
  );

  const runAll = useCallback(async () => {
    abortControllerRef.current?.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setTests(buildInitialTests());
    setTestsLocal(emptyTestsLocal);

    for (const config of TESTS) {
      if (controller.signal.aborted) return;
      await runTest(config, controller.signal);
    }
  }, [runTest, setTestsLocal]);

  useEffect(() => {
    runAll();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [runAll]);

  const results = TESTS.map(({ type }) => tests[type]).filter(
    (test) => test.data.status !== undefined,
  );
  const successCount = results.filter(
    (test) => test.data.status === true,
  ).length;
  const errorCount = results.filter(
    (test) => test.data.status === false,
  ).length;
  const finished = !isTesting && results.length === TESTS.length;

  return (
    <Stack
      sx={{
        display: "flex",
        width: "100%",
        flexDirection: "column",
        gap: 2,
        pt: 2,
      }}
    >
      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          backgroundColor: theme.palette.background.card,
          borderRadius: 1,
          padding: 1.5,
          width: "100%",
        }}
      >
        <Box sx={{ width: "75%" }}>
          <Stack
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "flex-start",
              gap: 1,
            }}
          >
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Dns fontSize="inherit" />
              <Typography color="text.secondary" fontSize={"14px"}>
                Roteador:
              </Typography>
              <Typography fontSize={"13px"}>{form?.sourceIp}</Typography>
            </Stack>
            <Typography display={{ xs: "none", sm: "flex" }}>|</Typography>
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Sensors fontSize="inherit" />
              <Typography color="text.secondary" fontSize={"14px"}>
                SNMP:
              </Typography>
              <Typography fontSize={"13px"}>
                {form?.snmpIp}
                {form?.snmpPort ? `:${form.snmpPort}` : ""}
              </Typography>
            </Stack>
          </Stack>
          {finished && (
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
              }}
            >
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 0.5,
                  alignItems: "center",
                  color: theme.palette.sucess.main,
                }}
              >
                <CheckCircleOutline fontSize="inherit" />
                <Typography fontSize={"14px"}>{successCount}</Typography>
              </Stack>
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 0.5,
                  alignItems: "center",
                  color: theme.palette.error.main,
                }}
              >
                <ErrorOutline fontSize="inherit" />
                <Typography fontSize={"14px"}>{errorCount}</Typography>
              </Stack>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            width: "25%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            disabled={isTesting}
            sx={{
              height: 48,
              backgroundColor: theme.palette.background.card,
              borderColor: theme.palette.text.primary,
              color: theme.palette.text.primary,
              ":hover": {
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
                borderColor: theme.palette.text.primary,
              },
            }}
            onClick={runAll}
            startIcon={<Replay />}
          >
            Testar novamente
          </Button>
        </Box>
      </Stack>

      {TESTS.map(({ type }) => (
        <CardConnectivity
          key={type}
          loading={tests[type].loading}
          testData={tests[type].data}
        />
      ))}

      {finished && errorCount > 0 && (
        <Alert
          severity="error"
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.error.main, 0.33)}`,
            fontSize: 13,
          }}
        >
          <AlertTitle fontSize={14}>
            {errorCount} {errorCount === 1 ? "Teste falhou" : "Testes falharam"}
          </AlertTitle>
          Todos os testes precisam passar para salvar o roteador. Volte, ajuste
          as configurações e teste novamente.
        </Alert>
      )}

      {finished && errorCount === 0 && (
        <Alert
          severity="success"
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.sucess.main, 0.33)}`,
            fontSize: 13,
          }}
        >
          <AlertTitle fontSize={14}>Todos os testes passaram</AlertTitle>O
          roteador está acessível — siga para salvar.
        </Alert>
      )}
    </Stack>
  );
}
